import {useState, useEffect, useCallback} from 'react';
import {useDispatch} from 'react-redux';
import dateFormat from 'dateformat';

// UTILS
import getParsedReview from '@stencil/hotels-utils/src/get-parsed-reviews';
import {isValidDate} from '@stencil/hotels-utils/src/date-utils/date-utils';

// CONFIG
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';

// HOOKS
import Modal, {useModal} from '@stencil/hotels-modal-component';

// COMPONENTS
import UserAvatar from '../../UserAvatar/UserAvatar';
import ImageGalleryPopupChunk from '../../ImageGalleryPopup/ImageGalleryPopupChunk';

// ICONS
import HappyInfoIconV2 from '@stencil/icons/src/HappyInfoIconV2/HappyInfoIconV2';

// STYLES
import {
  WrapperDiv,
  UserAvatarWrapperDiv,
  UserReviewOuterWrapperDiv,
  UserReviewWrapperDiv,
  UserDetailsAndRatingWrapperDiv,
  UserReviewImageListWrapperDiv,
  HotelierResponseWrapperDiv,
  RowWithMarginR10,
  StyledImage,
  UserNameStyle,
  StayedOnStyle,
  AskedByStyle,
  RatingTextStyle,
  UserReviewTextStyle,
  HotelierResponseContentStyle,
  HotelierNameTextStyle,
  HotelierResponseTextStyle,
  ViewAllWrapDiv,
  ViewAllText,
  InfoIconWrapper,
  HiddenTooltipWrapperDiv,
  VerifiedText,
  VerifiedIcon,
  TipPositioning,
  RatingWrapperDiv,
} from './UserReview.styles';
import {Column} from '.././../../common/Layouts/Layouts';
import {PrimaryLink} from '../../../../CommonStyleUtils/dwebCommon.styles';

const UserReview = ({reviewData, isUpsellFlow = false}) => {
  const {
    reviewText = '',
    rating = 0,
    images = [],
    responseToReview: responseToReviewData = [],
    travellerName = '',
    publishDate = '',
    travellerImage = '',
    reviewsWrittenInfo = '',
    logo,
    travelType = '',
    reviewSpanList = [],
  } = reviewData;

  const [showAll, toggleShowAll] = useState(false);
  const [galleryImages, updateGalleryImages] = useState([]);
  const [currentImageIndex, updateCurrentImageIndex] = useState(1);
  const [showAllReply, toggleShowAllReply] = useState(false);
  const replyCharLimit = 120;

  const dispatch = useDispatch();

  const imgReviewData = {
    firstName: travellerName,
    submittedAt: publishDate,
    reviewContent: reviewText,
  };

  const {
    isModalOpen: isReviewGalleryModalOpen,
    closeModal: closeReviewGalleryModal,
    openModal: openReviewGalleryModal,
  } = useModal();

  useEffect(() => {
    if (images.length > 0) {
      updateGalleryImages(images);
    }
  }, [images]);

  const showFullReview = useCallback(() => {
    toggleShowAll(!showAll);
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.UserReview}_${detailsClick.userReview.readMoreReview}`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.UserReview,
          destination: '',
        },
      }) as any,
    );
  }, [showAll]);

  const showFullReply = useCallback(() => {
    toggleShowAllReply(!showAllReply);
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.UserReview}_${detailsClick.userReview.readMoreReply}`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.UserReview,
          destination: '',
        },
      }) as any,
    );
  }, [showAllReply]);

  const pushGAForImageClick = () => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.UserReview}_${detailsClick.userReview.reviewImageClicked}`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.UserReview,
          destination: '',
        },
      }) as any,
    );
  };

  const travellerType = travelType ? `${travelType} Traveller` : '';
  const separator = travellerType && reviewsWrittenInfo ? '|' : '';
  const reviewInfo = reviewsWrittenInfo || '';

  const responseToReview = responseToReviewData?.[0] || null;
  const firstName = travellerName.split(/\s+/)[0];
  const lastName = travellerName.split(/\s+/)[1];

  return (
    <>
      <WrapperDiv
        itemScope
        itemProp="reviews"
        itemType="http://schema.org/Review"
        data-testid={`d-UserReview-item-${reviewData.id}`}
      >
        <UserAvatarWrapperDiv>
          <UserAvatar
            userImg={travellerImage}
            firstName={firstName}
            lastName={lastName}
            isUpsellFlow={isUpsellFlow}
          />
        </UserAvatarWrapperDiv>

        <UserReviewOuterWrapperDiv>
          <Column>
            <UserDetailsAndRatingWrapperDiv>
              <Column>
                <div>
                  <span itemProp="author" itemType="http://schema.org/Person" itemScope>
                    <UserNameStyle itemProp="name" content={`${travellerName}`}>
                      {travellerName}{' '}
                    </UserNameStyle>
                  </span>
                  {isValidDate(new Date(publishDate)) && (
                    <>
                      <StayedOnStyle noPadding={true}>
                        (Stayed {dateFormat(publishDate, 'd mmm, yyyy')})
                      </StayedOnStyle>
                      <meta
                        content={dateFormat(publishDate, 'yyyy-mm-dd')}
                        itemProp="datePublished"
                      />
                    </>
                  )}
                  {!!logo && (
                    <InfoIconWrapper>
                      <HappyInfoIconV2 width="1.8rem" height="1.8rem" margin="0 0 0 0.4rem" />
                      <HiddenTooltipWrapperDiv>
                        <TipPositioning />
                        <VerifiedText>Verified review powered by</VerifiedText>
                        <VerifiedIcon src={logo || ''} alt="icon" />
                      </HiddenTooltipWrapperDiv>
                    </InfoIconWrapper>
                  )}
                  <AskedByStyle>{`${travellerType} ${separator} ${reviewInfo}`}</AskedByStyle>
                </div>
              </Column>
              {rating > 0 && (
                <Column>
                  <RatingWrapperDiv
                    totalRating={rating}
                    itemProp="reviewRating"
                    itemScope
                    itemType="http://schema.org/Rating"
                  >
                    <RatingTextStyle itemProp="ratingValue">{rating}</RatingTextStyle>
                    <RatingTextStyle>/5</RatingTextStyle>
                  </RatingWrapperDiv>
                </Column>
              )}
            </UserDetailsAndRatingWrapperDiv>
            {reviewText && reviewText.length >= 500 && reviewText.length <= 600 && (
              <UserReviewWrapperDiv>
                <UserReviewTextStyle itemProp="reviewBody">
                  {getParsedReview(reviewText, {}, reviewSpanList)}
                </UserReviewTextStyle>
              </UserReviewWrapperDiv>
            )}
            {reviewText && !(reviewText.length >= 500 && reviewText.length <= 600) && (
              <UserReviewWrapperDiv>
                <UserReviewTextStyle>
                  {showAll
                    ? getParsedReview(reviewText, {}, reviewSpanList)
                    : getParsedReview(reviewText.substring(0, 500), {}, reviewSpanList)}
                  {(!showAll && reviewText.length > 500 && (
                    <PrimaryLink onClick={showFullReview}> ...more</PrimaryLink>
                  )) ||
                    ''}
                </UserReviewTextStyle>
              </UserReviewWrapperDiv>
            )}

            {images?.length > 0 && (
              <UserReviewImageListWrapperDiv>
                {images?.slice(0, 6)?.map((data, i) => {
                  let imgUrl = data?.imgUrl;
                  return (
                    <RowWithMarginR10 key={`${imgUrl}-${i}`} isUpsellFlow={isUpsellFlow}>
                      <StyledImage
                        src={imgUrl}
                        onClick={() => {
                          updateCurrentImageIndex(i + 1),
                            openReviewGalleryModal(),
                            pushGAForImageClick();
                        }}
                      />
                      {images?.length >= 6 && i === 6 && (
                        <ViewAllWrapDiv
                          onClick={() => {
                            updateCurrentImageIndex(i + 1),
                              openReviewGalleryModal(),
                              pushGAForImageClick();
                          }}
                        >
                          <ViewAllText>View All</ViewAllText>
                        </ViewAllWrapDiv>
                      )}
                    </RowWithMarginR10>
                  );
                })}
              </UserReviewImageListWrapperDiv>
            )}
            {!!responseToReview?.responseText?.length && (
              <HotelierResponseWrapperDiv>
                <HotelierResponseContentStyle>
                  <HotelierNameTextStyle>
                    {responseToReview.name} {responseToReview.repliedAgoText}
                  </HotelierNameTextStyle>
                  {responseToReview?.responseText &&
                    responseToReview?.responseText?.length <= replyCharLimit && (
                      <HotelierResponseTextStyle
                        dangerouslySetInnerHTML={{__html: responseToReview?.responseText}}
                      />
                    )}
                  {responseToReview?.responseText &&
                    responseToReview?.responseText?.length > replyCharLimit && (
                      <HotelierResponseTextStyle>
                        {showAllReply ? (
                          <span
                            dangerouslySetInnerHTML={{__html: responseToReview?.responseText}}
                          />
                        ) : (
                          <>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: responseToReview?.responseText?.substring(
                                  0,
                                  replyCharLimit,
                                ),
                              }}
                            />
                            <PrimaryLink onClick={showFullReply}> ...more</PrimaryLink>
                          </>
                        )}
                      </HotelierResponseTextStyle>
                    )}
                </HotelierResponseContentStyle>
              </HotelierResponseWrapperDiv>
            )}
          </Column>
        </UserReviewOuterWrapperDiv>
      </WrapperDiv>
      <Modal
        isOpen={isReviewGalleryModalOpen}
        closeModal={closeReviewGalleryModal}
        modalStyles={{
          width: '80vw',
        }}
      >
        {galleryImages.length > 0 ? (
          <ImageGalleryPopupChunk
            styleProps={{width: '80%'}}
            data={images}
            totalCount={images.length}
            isUserReview={true}
            tabsRequired={false}
            initialPage={currentImageIndex}
            reviewInfo={imgReviewData}
          />
        ) : (
          ''
        )}
      </Modal>
    </>
  );
};

export default UserReview;
